import React, {useState, useEffect, useRef} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useStyles } from './styles';

/** Components */
import Skeleton from '@mui/material/Skeleton';
import PageContainer from '../../components/PageContainer';
import { PageHeader, Panel } from '../../components/ui';
import CustomerHistoryDialog from '../../components/CustomerHistoryDialog';
import MockupViewControls from '../../components/MockupViewControls';
import MockupsCsvExport from '../../components/MockupsCsvExport';
/** Selectors */
import { Analytics3d }  from '../../store/slices'
import {IStore} from "../../store/types";
import Box from '@mui/material/Box';
import MockupHistoryFilters from "../../components/MockupHistoryFilters";
import Dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";

const DEFAULT_PAGING_STATE = {
    // limit: 50,
    maxDate: Dayjs(new Date()).format("YYYY-MM-DD"),
    minDate: Dayjs(new Date()).format("YYYY-MM-DD"),
};

const HtmlTooltip = Tooltip;

const MockupsPage = () => {
    const {
        analytics3d,
        status,
        queryParams
    } = useSelector<IStore, { analytics3d: any, status: string , queryParams: any}>(state => ({
        analytics3d: state.analytics3d.mockups,
        status: state.analytics3d.status,
        queryParams: state.analytics3d.mockups.queryParams
    }));
    const dispatch = useDispatch();
    const blockRef = useRef(null);
    const [columnsValue, setColumnsValue] = React.useState(4);
    const classes = useStyles({columnsValue});
    const [historyCustomerId, setHistoryCustomerId] = useState<string | null>(null);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customerId');
    // Ref для зберігання останнього requestId для перевірки актуальності результатів
    const lastRequestIdRef = useRef<string>('');
    const [validResults, setValidResults] = useState<any>(null);

    const loadOrders = (queryParams) => {
        // Зберігаємо requestId останнього запиту
        if (queryParams?.requestId) {
            lastRequestIdRef.current = queryParams.requestId;
        }
        dispatch<any>(Analytics3d.getMockups3d(queryParams));
    };
    
    // Перевіряємо, чи результати відповідають останньому запиту
    useEffect(() => {
        if (analytics3d?.result && queryParams?.requestId) {
            // Якщо requestId відповідає останньому запиту, зберігаємо результати
            if (queryParams.requestId === lastRequestIdRef.current) {
                setValidResults(analytics3d);
            }
            // Якщо requestId не відповідає, ігноруємо результати (вони від старого запиту)
        } else if (analytics3d?.result && !queryParams?.requestId) {
            // Якщо requestId відсутній (старий запит), все одно показуємо результати
            setValidResults(analytics3d);
        }
    }, [analytics3d, queryParams?.requestId]);

    const onLoadMore = (queryParams) => {
        dispatch<any>(Analytics3d.addMockups3d({...queryParams, lastEvaluatedKey: analytics3d.lastEvaluatedKey}));
    }

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Викликаємо функцію дозавантаження
                    onLoadMore(queryParams);
                }
            },
            { threshold: .01 } // Відсоток видимості (100%)
        );

        if (blockRef.current) {
            observer.observe(blockRef.current);
        }

        return () => {
            if (blockRef.current) {
                observer.unobserve(blockRef.current);
            }
        };
    }, [onLoadMore]);

    const showPopup = (id: string | number) => setHistoryCustomerId(String(id));
    useEffect(() => {
        if (customerId) showPopup(customerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- open the dialog for the id in the URL once
    }, []);


    useEffect(() => {
        loadOrders({
            ...queryParams,
            // limit: queryParams?.limit || DEFAULT_PAGING_STATE.limit,
            minDate: queryParams?.minDate || DEFAULT_PAGING_STATE.minDate,
            maxDate: queryParams?.maxDate || DEFAULT_PAGING_STATE.maxDate,
            // orderExist: queryParams?.orderExist || DEFAULT_PAGING_STATE.orderExist,
        });
    }, [])

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Mockups"
                title="3D mockups"
                subtitle="Legacy history endpoint; the newer previews are under Mockups preview."
                actions={<MockupsCsvExport queryParams={{...DEFAULT_PAGING_STATE, ...queryParams}} loading={status === 'loading'} />}
            />
            <CustomerHistoryDialog
                open={!!historyCustomerId}
                customerId={historyCustomerId}
                onClose={() => setHistoryCustomerId(null)}
                range={{ minDate: queryParams?.minDate, maxDate: queryParams?.maxDate }}
            />
            <Panel padding="none" actions={<MockupViewControls columns={columnsValue} onColumns={setColumnsValue} />}>
                <MockupHistoryFilters queryParams={{...DEFAULT_PAGING_STATE, ...queryParams}} loadMockups={loadOrders} loading={status === 'loading'} />
            </Panel>
            {(validResults?.result && status !== 'loading') &&
                <Box sx={{ width: '100%', typography: 'body1', maxHeight: '70vh', overflow: 'scroll', border: 1, borderColor: 'divider', borderRadius: '10px', mt: 2 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', padding: '10px'}}>
                        {validResults?.result &&
                            validResults.result.map((event) => (
                                <div className={classes.blockWrapper} key={event.id}>
                                    {event?.mockupUrl ? (
                                        <img loading="lazy" style={{maxHeight: '100%', maxWidth: '100%', width: 'auto'}} src={event.mockupUrl} alt="Mockup"/>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: "column"}}>
                                            <div>Upload with issue</div>
                                            <div>Event ID :</div>
                                            <div>{event.id}</div>
                                        </div>
                                    )}
                                    <div className={classes.itemNav}>
                                        {event.toCart && <div className={classes.addedToCart}>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        {`Added to cart: ${new Date(event.toCart).toLocaleString()}`}
                                                    </React.Fragment>
                                                }
                                            >
                                               <span>✔</span>
                                            </HtmlTooltip>
                                        </div>}
                                        {event.orderDate && <div className={classes.ordered}>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        {`Ordered: ${new Date(event.orderDate).toLocaleString()}`}
                                                    </React.Fragment>
                                                }
                                            >
                                                <span>$</span>
                                            </HtmlTooltip>
                                            </div>}
                                    </div>
                                    <div className={classes.itemNavBottom}>
                                        {event.additionalEvents &&
                                            <div className={classes.editButton} onClick={() => {showPopup(event.customerId)}}>History</div>
                                        }
                                    </div>
                                </div>
                            ))
                        }
                        {analytics3d?.lastEvaluatedKey &&
                            <div className={classes.blockWrapper} ref={blockRef}>
                                <Skeleton animation="wave" width={'100%'} height={'98%'} variant="rounded" />
                            </div>
                        }
                    </div>
                    {analytics3d?.result.length < 1 && status !== 'loading' && <div style={{margin: 20}}>Don't found result</div>}
                </Box>
            }
            {status === 'loading' &&
                <Skeleton animation="wave" width={'100%'} height={600} variant="rounded" />}
        </PageContainer>
    );
};

export default MockupsPage;
